import { useState } from 'react'
import { Drawer, Indicator, Tooltip } from '@mantine/core'
import {
    AlertTriangle,
    Bell01,
    Check,
    Mail01,
    Mail04,
    MessageChatCircle,
    Package,
    Settings01,
    ShoppingCart01,
    Tool01,
} from '@untitled-ui/icons-react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import useSWR from 'swr'

import { useAxiosApi } from '@/hooks/useAxiosApi'
import useMe from '@/hooks/useMe'
import useProfiles from '@/hooks/useProfiles'
import CommentContent from '@/components/ui/CommentContent'

dayjs.extend(relativeTime)

const NOTIFICATION_ICONS = {
    mention: MessageChatCircle,
    stockout: AlertTriangle,
    address_error: AlertTriangle,
    assembly_control: Tool01,
    assembly_done: Check,
    purchase_received: Package,
    retailer_order: ShoppingCart01,
}

export default function SidebarNotificationsButton() {
    const { requestGet, requestPost } = useAxiosApi()
    const navigate = useNavigate()
    const { isAdmin } = useMe()
    const { profilesMap } = useProfiles()

    const [opened, setOpened] = useState(false)

    // Poll unread count every 30s
    const { data: unreadData, mutate: mutateUnread } = useSWR(
        isAdmin ? '/me/notifications/unread-count' : null,
        requestGet,
        { refreshInterval: 30000 }
    )
    const unreadCount = unreadData?.data?.unread_count ?? 0

    // Fetch full list only when drawer is open
    const { data: notifData, isLoading, mutate: mutateNotifications } = useSWR(
        isAdmin && opened ? '/me/notifications' : null,
        requestGet,
    )
    const notifications = notifData?.data?.notifications ?? []

    if (!isAdmin) return null

    const updateNotifications = (mapFn) => {
        mutateNotifications((prev) => {
            if (!prev?.data?.notifications) return prev
            return { ...prev, data: { ...prev.data, notifications: prev.data.notifications.map(mapFn) } }
        }, false)
    }

    const adjustUnreadCount = (delta) => {
        mutateUnread((prev) => ({
            data: { unread_count: Math.max(0, (prev?.data?.unread_count ?? 0) + delta) },
        }), false)
    }

    const markAllAsRead = async () => {
        await requestPost('/me/notifications/read-all')
        mutateUnread({ data: { unread_count: 0 } }, false)
        updateNotifications((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
    }

    const toggleRead = async (e, notification) => {
        e.stopPropagation()
        const wasRead = !!notification.read_at
        await requestPost(`/me/notifications/${notification.id}/toggle-read`)
        adjustUnreadCount(wasRead ? 1 : -1)
        updateNotifications((n) =>
            n.id === notification.id
                ? { ...n, read_at: wasRead ? null : new Date().toISOString() }
                : n
        )
    }

    const handleNotificationClick = async (notification) => {
        if (!notification.read_at) {
            await requestPost(`/me/notifications/${notification.id}/toggle-read`)
            adjustUnreadCount(-1)
            updateNotifications((n) =>
                n.id === notification.id
                    ? { ...n, read_at: new Date().toISOString() }
                    : n
            )
        }

        if (notification.entity_route) {
            navigate(notification.entity_route)
            setOpened(false)
        }
    }

    return (
        <>
            <button
                onClick={(e) => {
                    e.stopPropagation()
                    setOpened(true)
                }}
                className="flex items-center justify-center w-[38px] h-[38px] shrink-0 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
            >
                <Indicator
                    disabled={unreadCount === 0}
                    label={unreadCount}
                    size={16}
                    color="red"
                >
                    <Bell01
                        width={20}
                        height={20}
                        className="text-gray-500"
                    />
                </Indicator>
            </button>

            <Drawer
                opened={opened}
                onClose={() => setOpened(false)}
                position="right"
                size={480}
                title={
                    <div className="flex items-center justify-between w-full pr-4">
                        <span>Notifications</span>
                        <Tooltip label="Préférences" position="bottom" withArrow>
                            <button
                                onClick={() => {
                                    navigate('/me')
                                    setOpened(false)
                                }}
                                className="p-1 rounded-sm hover:bg-gray-100 cursor-pointer transition-colors text-gray-400 hover:text-gray-600"
                            >
                                <Settings01 width={16} height={16} />
                            </button>
                        </Tooltip>
                    </div>
                }
                styles={{
                    body: { padding: 0, display: 'flex', flexDirection: 'column', height: 'calc(100% - 60px)' },
                }}
            >
                <div className="flex-1 overflow-y-auto">
                    {isLoading && notifications.length === 0 && (
                        <div className="px-4 py-12 text-center text-sm text-gray-400">
                            Chargement…
                        </div>
                    )}

                    {!isLoading && notifications.length === 0 && (
                        <div className="px-4 py-12 text-center text-sm text-gray-400">
                            Aucune notification
                        </div>
                    )}

                    {notifications.map((notification) => {
                        const Icon = NOTIFICATION_ICONS[notification.type] || Bell01
                        return (
                        <div
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            className="px-4 py-3.5 flex items-start gap-3 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100"
                        >
                            <div className={`shrink-0 mt-0.5 ${
                                notification.type === 'stockout' || notification.type === 'address_error'
                                    ? 'text-red-500'
                                    : 'text-gray-400'
                            }`}>
                                <Icon width={16} height={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-gray-600">
                                    {notification.segments?.map((seg, i) => (
                                        seg.bold
                                            ? <span key={i} className="font-medium text-gray-900">{seg.text}</span>
                                            : <span key={i}>{seg.text}</span>
                                    ))}
                                </div>

                                {notification.comment_content && (
                                    <div className="mt-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 text-sm text-gray-600">
                                        <CommentContent
                                            content={notification.comment_content}
                                            profilesMap={profilesMap}
                                        />
                                    </div>
                                )}

                                <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                                    {!notification.read_at && (
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                                    )}
                                    <span>
                                        {dayjs(notification.created_at).fromNow()}
                                    </span>
                                    <Tooltip
                                        label={notification.read_at ? 'Marquer comme non lu' : 'Marquer comme lu'}
                                        position="bottom"
                                        withArrow
                                    >
                                        <button
                                            onClick={(e) => toggleRead(e, notification)}
                                            className="p-0.5 rounded-sm hover:bg-gray-100 cursor-pointer transition-colors text-gray-300 hover:text-gray-500"
                                        >
                                            {notification.read_at ? (
                                                <Mail01 width={14} height={14} />
                                            ) : (
                                                <Mail04 width={14} height={14} />
                                            )}
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                        )
                    })}
                </div>

                {unreadCount > 0 && (
                    <div className="shrink-0 border-t border-gray-100 px-4 py-3">
                        <button
                            onClick={markAllAsRead}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                        >
                            <Mail04 width={15} height={15} />
                            Tout marquer comme lu
                        </button>
                    </div>
                )}
            </Drawer>
        </>
    )
}
